// src/sectors/pilates/members/components/paymentReceive/MemberSessionPickerStep.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

// ---------- helpers ----------
function hm(min) {
  const m = Number(min || 0);
  const h = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${h}:${mm}`;
}
function todayLocalDateStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}
function trMoney(n) {
  const x = Number(n || 0);
  return `₺${x.toLocaleString('tr-TR')}`;
}
function numOr0(v) {
  const x = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(x) ? x : 0;
}
function normalizeRow(x) {
  if (!x) return null;
  const debt = x.sessionDebtTry ?? x.debtTry ?? 0;
  return {
    sessionId: x.sessionId ?? null,
    dayISO: x.dayISO ?? null,
    startMin: x.startMin ?? null,
    durationMin: x.durationMin ?? null,
    activityName: x.activityName ?? null,
    trainerId: x.trainerId ?? null,
    trainerName: x.trainerName ?? null,

    sessionParticipantId: x.sessionParticipantId ?? x.id ?? null,
    memberId: x.memberId ?? null,
    fullName: x.fullName ?? null,
    phone: x.phone ?? null,

    totalTry: x.totalTry ?? null,
    paidTry: x.paidTry ?? 0,

    sessionDebtTry: Number(debt || 0),
    paymentStatus: x.paymentStatus ?? null,
  };
}

function MethodChips({ method, setMethod }) {
  const theme = useMuiTheme();
  const items = [
    ['CASH', 'Nakit', 'payments'],
    ['CARD', 'Kart', 'credit_card'],
    ['TRANSFER', 'Havale', 'swap_horiz'],
  ];
  return (
    <MDBox display="flex" gap={1} flexWrap="wrap">
      {items.map(([key, label, icon]) => {
        const active = method === key;
        return (
          <Chip
            key={key}
            icon={<Icon sx={{ fontSize: 18 }}>{icon}</Icon>}
            label={label}
            onClick={() => setMethod(key)}
            sx={{
              height: 34,
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 800,
              border: 'none',
              background: active
                ? alpha(theme.palette.warning.main, 0.14)
                : alpha(theme.palette.grey[500], 0.1),
            }}
          />
        );
      })}
    </MDBox>
  );
}
MethodChips.propTypes = {
  method: PropTypes.string.isRequired,
  setMethod: PropTypes.func.isRequired,
};

export function MemberSessionPickerStep({ memberId, onDraftChange, limit = 200 }) {
  const theme = useMuiTheme();

  const [q, setQ] = useState('');
  const [method, setMethod] = useState('CASH');

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const [onlyDebt, setOnlyDebt] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [amountTry, setAmountTry] = useState('');
  const [note, setNote] = useState('');

  const tRef = useRef(null);

  const load = async (queryStr) => {
    try {
      setLoading(true);
      setErr('');

      // ✅ Eğer backend memberId filter destekliyorsa buraya ekleyebilirsin:
      // const res = await sessionsApi.pendingSessionPayments({ q: queryStr || undefined, limit, memberId });
      const res = await sessionsApi.pendingSessionPayments({
        q: queryStr || undefined,
        limit,
      });

      const arr = Array.isArray(res) ? res : res?.items ?? res?.content ?? [];
      const normalized = (Array.isArray(arr) ? arr : []).map(normalizeRow).filter(Boolean);

      // ✅ Asıl istek: SADECE seçili üyenin seansları
      const filteredByMember = normalized.filter(
        (r) => String(r?.memberId ?? '') === String(memberId ?? '')
      );

      setRows(filteredByMember);

      setSelected((prev) => {
        if (!prev?.sessionParticipantId) return prev;
        const ok = filteredByMember.some(
          (r) => r.sessionParticipantId === prev.sessionParticipantId
        );
        return ok ? prev : null;
      });
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Seans borçları alınamadı');
      setRows([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  // ilk load + member değişince reload
  useEffect(() => {
    if (!memberId) {
      setRows([]);
      setSelected(null);
      setErr('Üye seçili değil.');
      return;
    }
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // arama debounce
  useEffect(() => {
    if (!memberId) return;
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => load(q), 250);
    return () => tRef.current && clearTimeout(tRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, memberId]);

  const filtered = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    const list = onlyDebt ? base.filter((r) => Number(r.sessionDebtTry || 0) > 0) : base;
    return list.sort((a, b) => Number(b.sessionDebtTry || 0) - Number(a.sessionDebtTry || 0));
  }, [rows, onlyDebt]);

  const debt = Number(selected?.sessionDebtTry || 0);

  // seçim değişince default amount/note hazırla
  useEffect(() => {
    if (!selected?.sessionParticipantId) {
      setAmountTry('');
      setNote('');
      onDraftChange?.(null);
      return;
    }
    setAmountTry(debt > 0 ? String(debt) : '');
    setNote(
      `Seans tahsilatı (${selected.dayISO} ${hm(selected.startMin)} • ${
        selected.activityName || 'Seans'
      })`
    );
    // draft aşağıdaki effect’te üretilecek
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.sessionParticipantId]);

  // ✅ Draft üret: PaymentReceiveModal confirm butonunu besler
  useEffect(() => {
    if (!selected?.sessionParticipantId) {
      onDraftChange?.(null);
      return;
    }

    const amount = numOr0(amountTry);
    const canPay = debt > 0 && amount > 0 && amount <= debt;

    if (!canPay) {
      onDraftChange?.(null);
      return;
    }

    const title = `${selected.fullName || 'Üye'} • Seans Borç Tahsilatı`;
    const detail = `#${selected.sessionParticipantId} • ${selected.dayISO} ${hm(
      selected.startMin
    )} • ${selected.activityName || 'Seans'} • ${trMoney(amount)} • ${method}`;

    onDraftChange?.({
      kind: 'SESSION_DEBT',
      amountTry: Math.round(amount),
      title,
      detail,
      action: async () => {
        await sessionsApi.receiveSessionPayment({
          sessionParticipantId: selected.sessionParticipantId,
          amountTry: Math.round(amount),
          method,
          note: (note || '').trim() || undefined,
          paidDate: todayLocalDateStr(),
        });
      },
    });
  }, [selected, amountTry, note, method, debt, onDraftChange]);

  return (
    <MDBox sx={{ display: 'grid', gap: 1.2 }}>
      <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <MDInput
          label="Ara (seans / gün / katılımcıId)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          fullWidth
        />

        <Chip
          label={onlyDebt ? 'Sadece borçlu' : 'Hepsi'}
          onClick={() => setOnlyDebt((x) => !x)}
          sx={{
            height: 34,
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 800,
            border: 'none',
            background: onlyDebt
              ? alpha(theme.palette.warning.main, 0.14)
              : alpha(theme.palette.grey[500], 0.12),
          }}
        />
      </MDBox>

      <MethodChips method={method} setMethod={setMethod} />

      <MDBox sx={{ maxHeight: 280, overflow: 'auto', pr: 0.5 }}>
        {loading ? (
          <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <MDTypography variant="caption" sx={{ ml: 1, opacity: 0.75 }}>
              Yükleniyor…
            </MDTypography>
          </MDBox>
        ) : err ? (
          <MDBox
            sx={{
              p: 1.2,
              borderRadius: '14px',
              background: alpha(theme.palette.error.main, 0.08),
            }}
          >
            <MDTypography variant="caption" color="error">
              {err}
            </MDTypography>
            <MDButton
              variant="text"
              color="error"
              onClick={() => load(q)}
              sx={{ mt: 0.5, borderRadius: '12px' }}
            >
              Tekrar dene
            </MDButton>
          </MDBox>
        ) : (
          <>
            {filtered.map((r) => {
              const active = selected?.sessionParticipantId === r.sessionParticipantId;
              const d = Number(r.sessionDebtTry || 0);

              return (
                <MDBox
                  key={r.sessionParticipantId}
                  onClick={() => setSelected(r)}
                  sx={{
                    p: 1.05,
                    borderRadius: '16px',
                    border: 'none',
                    background: active
                      ? alpha(theme.palette.warning.main, 0.12)
                      : alpha(theme.palette.grey[500], 0.06),
                    cursor: 'pointer',
                    mb: 0.7,
                  }}
                >
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDTypography variant="button" fontWeight="bold">
                        {r.dayISO} {hm(r.startMin)} • {r.activityName || 'Seans'}
                      </MDTypography>

                      <MDTypography variant="caption" sx={{ opacity: 0.78, display: 'block' }}>
                        KatılımcıId: #{r.sessionParticipantId} • Eğitmen: {r.trainerName || '—'}
                      </MDTypography>

                      <MDTypography
                        variant="caption"
                        sx={{ opacity: 0.9, display: 'block', mt: 0.2 }}
                      >
                        Borç: {trMoney(d)} (Toplam {trMoney(r.totalTry)} • Ödenen{' '}
                        {trMoney(r.paidTry)})
                      </MDTypography>
                    </MDBox>

                    <Icon sx={{ opacity: active ? 1 : 0.25 }}>check_circle</Icon>
                  </MDBox>
                </MDBox>
              );
            })}

            {!filtered.length ? (
              <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                Bu üyeye ait ödeme bekleyen seans bulunamadı.
              </MDTypography>
            ) : null}
          </>
        )}
      </MDBox>

      <Divider />

      {/* ✅ Seçili seans için ödeme formu */}
      <MDBox
        sx={{
          p: 1.15,
          borderRadius: '16px',
          background: alpha(
            theme.palette.background.paper,
            theme.palette.mode === 'dark' ? 0.25 : 0.7
          ),
          border: 'none',
          display: 'grid',
          gap: 1,
        }}
      >
        <MDTypography variant="button" fontWeight="bold">
          Ödeme Hazırla
        </MDTypography>

        <MDInput
          label="Alınan Ücret (₺)"
          value={amountTry}
          onChange={(e) => setAmountTry(e.target.value)}
          type="number"
          fullWidth
          disabled={!selected}
          inputProps={{ min: 0, max: debt || undefined }}
        />

        <MDInput
          label="Açıklama (opsiyonel)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          disabled={!selected}
        />

        {!selected ? (
          <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
            Yukarıdan bir seans seç.
          </MDTypography>
        ) : debt <= 0 ? (
          <MDTypography variant="caption" sx={{ color: 'error.main' }}>
            Bu seçili seans için borç görünmüyor.
          </MDTypography>
        ) : numOr0(amountTry) > debt ? (
          <MDTypography variant="caption" sx={{ color: 'error.main' }}>
            Tutar borçtan büyük olamaz.
          </MDTypography>
        ) : (
          <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
            Hazırsa alttaki “Ödemeyi Al” butonuna basınca onay ekranı gelecek.
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  );
}

MemberSessionPickerStep.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDraftChange: PropTypes.func.isRequired,
  limit: PropTypes.number,
};
